import { demoRequestInput, isAppErrorCode } from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import { MailCheckIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Field } from "@/components/app/field";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toLanguage } from "@/i18n";
import { publicDemo, useMe } from "@/lib/account";
import { errorMessage } from "@/lib/errors";
import { useLocation } from "@/lib/pages";
import { useSystemInfo } from "@/lib/system";
import { useTRPC, useTRPCClient } from "@/lib/trpc";
import { AuthCard } from "./account-app";
import { FormError, fieldErrors, Notice } from "./sign-in";

/**
 * The public demo (ADR 0012). Its request page stands where the sign-in form would: someone
 * enters an email and opens the link it gets, which leads into a demo garage of their own.
 */

/** "48 hours", or minutes for a short demo (BT_DEMO_GARAGE_HOURS below 1). */
function useDemoDuration(): string {
  const { t } = useTranslation();
  const hours = publicDemo()?.garageHours ?? 48;
  return hours >= 1
    ? t("demo.hours", { count: hours })
    : t("demo.minutes", { count: Math.max(1, Math.round(hours * 60)) });
}

/** /account/demo: asks for a link to a demo garage. */
export function DemoRequestPage() {
  const { t, i18n } = useTranslation();
  const client = useTRPCClient();
  const { search } = useLocation();
  const { data: me } = useMe();
  const privacyUrl = useSystemInfo()?.capabilities.privacyUrl;
  const duration = useDemoDuration();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  // Signed in: a visitor goes back into their demo garage (or its welcome steps), an admin to
  // their garages.
  useEffect(() => {
    if (me?.status !== "active") return;
    if (!me.demo) window.location.replace("/");
    else window.location.replace(me.demo.garage ? `/${me.demo.garage}/` : "/account/welcome");
  }, [me]);

  const code = search.get("error");
  const linkError = isAppErrorCode(code) ? t(`errors.${code}`) : null;

  if (sentTo) {
    return (
      <AuthCard title={t("demo.sentTitle")} body={t("demo.sentBody", { email: sentTo })}>
        <div className="flex flex-col items-center gap-4">
          <MailCheckIcon className="size-8 text-brand" aria-hidden />
          <button
            type="button"
            className="text-sm text-brand hover:underline"
            onClick={() => {
              setSentTo(null);
              setEmail("");
            }}
          >
            {t("demo.otherEmail")}
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("demo.requestTitle")} body={t("demo.requestBody", { duration })}>
      {search.get("ended") ? <Notice>{t("demo.ended")}</Notice> : null}
      <form
        className="flex flex-col gap-4"
        onSubmit={async (event) => {
          event.preventDefault();
          // The email comes in the language on screen; the welcome steps ask for the app's.
          const language = toLanguage(i18n.language);
          const parsed = demoRequestInput.safeParse({ email, language, website });
          if (!parsed.success) {
            setErrors(fieldErrors(parsed.error.issues));
            return;
          }
          setErrors({});
          setServerError(null);
          setPending(true);
          try {
            await client.publicDemo.request.mutate(parsed.data);
            setSentTo(parsed.data.email);
          } catch (error) {
            setServerError(errorMessage(error, t));
          } finally {
            setPending(false);
          }
        }}
      >
        <FormError message={linkError} />
        <Field label={t("demo.email")} htmlFor="demo-email" error={errors.email}>
          <Input
            id="demo-email"
            autoFocus
            inputMode="email"
            autoComplete="email"
            value={email}
            aria-invalid={errors.email ? true : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        {/* A field people never see: bots that fill it get the usual answer and no email. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -left-[9999px] h-px w-px overflow-hidden"
        >
          <label htmlFor="demo-website">{t("demo.honeypot")}</label>
          <input
            id="demo-website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>
        <FormError message={serverError} />
        <Button type="submit" size="lg" disabled={pending}>
          {t("demo.send")}
        </Button>
        <p className="text-xs text-pretty text-muted-foreground">
          {t("demo.stored")}
          {privacyUrl ? (
            <>
              {" "}
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener"
                className="font-medium text-foreground underline underline-offset-2 hover:text-brand"
              >
                {t("demo.privacy")}
              </a>
            </>
          ) : null}
        </p>
      </form>
    </AuthCard>
  );
}

/** "Reset demo data", confirmed: the garage gets the example data again. */
export function ResetDemoDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const reset = useMutation(trpc.publicDemo.reset.mutationOptions());
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("demo.resetTitle")}
      description={t("demo.resetBody")}
      confirmLabel={t("demo.reset")}
      destructive
      pending={reset.isPending}
      onConfirm={() =>
        reset.mutate(undefined, {
          onSuccess: () => {
            onOpenChange(false);
            toast.success(t("demo.resetDone"));
          },
        })
      }
    />
  );
}

/**
 * "End demo now", confirmed. It goes to the request page itself: refreshing the open queries
 * first would find the visitor signed out and race it there.
 */
export function EndDemoDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const client = useTRPCClient();
  const [pending, setPending] = useState(false);
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("demo.endTitle")}
      description={t("demo.endBody")}
      confirmLabel={t("demo.end")}
      destructive
      pending={pending}
      onConfirm={async () => {
        setPending(true);
        try {
          await client.publicDemo.end.mutate();
          window.location.assign("/account/demo?ended=1");
        } catch (error) {
          toast.error(errorMessage(error, t));
          setPending(false);
        }
      }}
    />
  );
}
